import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MailCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/language-switcher';
import { resolveAuthOriginFromCheckout } from '@/lib/checkout-origin';
import { useAuthPageTheme } from '@/hooks/useAuthPageTheme';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { useProvider } from '@/hooks/useTenant';
import { cn } from '@/lib/utils';

type CheckoutSummaryItem = {
  label: string;
  value: string;
};

type CheckoutSuccessState = {
  summaryItems?: CheckoutSummaryItem[];
  paymentRequired?: boolean;
  createdAt?: number;
  stripePaymentIntentId?: string | null;
  stripeSubscriptionId?: string | null;
  stripeSetupIntentId?: string | null;
};

function ProviderBrandMark({ size = 'lg' }: { size?: 'sm' | 'lg' }) {
  const provider = useProvider();
  const { t } = useTranslation();
  const providerName = provider?.name ?? t('customers.customer', 'Customer');
  const logo = useBrandLogo();
  const dimension = size === 'lg' ? 200 : 160;

  if (logo) {
    return (
      <img
        src={logo}
        alt={`${providerName} logo`}
        className="object-contain"
        style={{ maxWidth: dimension, maxHeight: 44 }}
      />
    );
  }

  return (
    <span className={cn('font-semibold tracking-tight', size === 'lg' ? 'text-2xl' : 'text-xl')}>
      {providerName}
    </span>
  );
}

export default function CheckoutSuccessPage() {
  useAuthPageTheme({ forcedTheme: 'light' });
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as CheckoutSuccessState | null) ?? null;

  useEffect(() => {
    if (i18n.language !== 'de') {
      void i18n.changeLanguage('de');
    }
  }, [i18n, i18n.language]);

  const [resolvedState, setResolvedState] = useState<CheckoutSuccessState | null>(state);

  useEffect(() => {
    if (state) {
      setResolvedState(state);
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem('checkout.success', JSON.stringify(state));
        } catch {
          // Ignore storage errors (private mode, quota, etc.)
        }
      }
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    try {
      const stored = window.sessionStorage.getItem('checkout.success');
      if (stored) {
        const parsed = JSON.parse(stored) as CheckoutSuccessState;
        setResolvedState(parsed);
        return;
      }
    } catch {
      // Ignore storage errors and fall through to redirect.
    }

    navigate('/customer/register', { replace: true });
  }, [navigate, state]);

  const summaryItems = useMemo(
    () =>
      (resolvedState?.summaryItems ?? []).filter(
        (item): item is CheckoutSummaryItem => Boolean(item?.label && item?.value),
      ),
    [resolvedState?.summaryItems],
  );
  const paymentRequired = Boolean(resolvedState?.paymentRequired);
  const stripePaymentIntentId = resolvedState?.stripePaymentIntentId ?? null;
  const stripeSubscriptionId = resolvedState?.stripeSubscriptionId ?? null;
  const stripeSetupIntentId = resolvedState?.stripeSetupIntentId ?? null;

  const handleCustomerLogin = () => {
    const authOrigin = resolveAuthOriginFromCheckout();
    if (authOrigin) {
      window.location.assign(new URL('/customer/login', authOrigin).toString());
      return;
    }
    navigate('/customer/login');
  };

  const handleHome = () => {
    navigate('/');
  };

  const infoTitle = t('checkout.verifyPrompt');
  const infoBody = t('checkout.verifyBody');
  const InfoIcon = MailCheck;
  const successSubtitle = t('checkout.successSubtitle');

  if (!resolvedState) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSwitcher />
      </div>

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ProviderBrandMark size="sm" />
            <Badge variant="outline" className="text-xs">
              {t('checkout.secure')}
            </Badge>
          </div>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="space-y-2">
            <CardTitle>{t('checkout.successTitle')}</CardTitle>
            {successSubtitle ? <CardDescription>{successSubtitle}</CardDescription> : null}
          </CardHeader>
          <CardContent className="space-y-6">
            {summaryItems.length > 0 ? (
              <div className="rounded-lg border p-4">
                <p className="font-medium">{t('checkout.review')}</p>
                <div className="mt-3 space-y-2 text-sm">
                  {summaryItems.map((item) => (
                    <div key={`${item.label}-${item.value}`} className="flex items-center justify-between">
                      <span className="text-muted-foreground">{item.label}</span>
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {stripePaymentIntentId || stripeSubscriptionId || stripeSetupIntentId ? (
              <div className="rounded-lg border p-4 text-sm">
                <p className="font-medium">{t('checkout.supportDetails')}</p>
                <div className="mt-3 space-y-2">
                  {stripePaymentIntentId ? (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">{t('checkout.paymentIntentId')}</span>
                      <span className="font-mono text-xs">{stripePaymentIntentId}</span>
                    </div>
                  ) : null}
                  {stripeSubscriptionId ? (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">{t('checkout.subscriptionId')}</span>
                      <span className="font-mono text-xs">{stripeSubscriptionId}</span>
                    </div>
                  ) : null}
                  {stripeSetupIntentId ? (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">{t('checkout.setupIntentId')}</span>
                      <span className="font-mono text-xs">{stripeSetupIntentId}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {!paymentRequired ? (
              <div className="rounded-lg border bg-primary/5 p-4 text-sm">
                <div className="flex items-center gap-2 text-primary">
                  <InfoIcon className="h-4 w-4" aria-hidden="true" />
                  <p className="font-semibold">{infoTitle}</p>
                </div>
                <p className="text-muted-foreground">{infoBody}</p>
              </div>
            ) : null}

            <div className="flex flex-wrap justify-center gap-3 sm:justify-end">
              <Button variant="secondary" onClick={handleHome}>
                {t('common.home')}
              </Button>
              <Button onClick={handleCustomerLogin}>
                {t('checkout.goToLogin')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
